import React from 'react';
import { Box, Container, Typography, Grid, Paper, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { Activity, Database, Briefcase, BarChart3, Repeat, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const integrations = [
  {
    icon: <Database size={32} color="#4776E6" />,
    title: '1. The Commerce Engine (POS & Inventory)',
    function: 'A lightning-fast checkout interface for walk-in customers and a comprehensive B2B invoicing portal, backed by a strict inventory database.',
    triggers: [
      {
        name: 'Inventory Trigger',
        desc: 'The moment a POS receipt is printed or an invoice is approved, the system instantly deducts the exact item quantities from your Inventory store.'
      },
      {
        name: 'Ledger Trigger',
        desc: 'Cash sales are instantly injected into your \'Cashbook\' as an Inward transaction.'
      },
      {
        name: 'Tax Trigger',
        desc: 'GST components (CGST/SGST) are automatically calculated and pushed to your tax liability reports.'
      }
    ]
  },
  {
    icon: <Repeat size={32} color="#2ECC71" />,
    title: '2. Smart Bank Reconciliation (Data Import)',
    function: 'An intelligent drag-and-drop ingestion engine that parses raw bank CSV statements and maps them to your accounting schema.',
    triggers: [
      {
        name: 'Invoice Auto-Matching',
        desc: 'When a bank row shows an Inward/Credit amount, the system automatically scans your Receivables store. If it finds a matching unpaid invoice, it flags it as a \'Suggested Match\'.'
      },
      {
        name: 'One-Click Settlement',
        desc: 'Approving the match simultaneously marks the invoice as \'Paid\', drops the client\'s outstanding balance to zero, and logs the verified transaction into your Bankbook.'
      }
    ]
  },
  {
    icon: <Briefcase size={32} color="#FF6B35" />,
    title: '3. The Auto-Settlement HR Engine (Expenses & Payroll)',
    function: 'A complete workforce management tool allowing staff to raise expense claims, request advances, and log partial cash returns.',
    triggers: [
      {
        name: 'Cash Flow Trigger',
        desc: 'When an Admin grants a \'Cash Advance\' to a staff member, it instantly writes an Outward transaction to the company Cashbook.'
      },
      {
        name: 'Payroll Interception',
        desc: 'When you click \'Generate Salary\' at the end of the month, the engine intercepts the calculation. It automatically subtracts any approved employee expenses from their pending advances. If they owe the company money, it dynamically injects an \'Unrecovered Advance Deduction\' into their payslip.'
      },
      {
        name: 'Ledger Closure',
        desc: 'Marking a salary as paid permanently locks the expenses and clears the employee\'s advance balance.'
      }
    ]
  },
  {
    icon: <BarChart3 size={32} color="#8E54E9" />,
    title: '4. Master Analytics Command Center',
    function: 'The default dashboard for Admins, displaying critical KPIs, exposure tracking, and cashflow visualizations.',
    triggers: [
      {
        name: 'Live Profit Calculation',
        desc: 'It doesn\'t just show total sales. It pulls your Gross Revenue (from POS/Invoices) and subtracts your Purchases and Payroll runs in real-time to display your true Net Profit.'
      },
      {
        name: 'Predictive Alerts',
        desc: 'It continuously scans the Inventory module behind the scenes to populate the \'Low Stock\' widget, ensuring Admins see supply chain issues before they halt operations.'
      }
    ]
  }
];

const Features: React.FC = () => {
  const theme = useTheme();

  return (
    <Box sx={{ width: '100%', overflow: 'hidden', bgcolor: '#f8f9fa' }}>
      <Helmet>
        <title>Akmo Books Features | The Connected Cloud ERP</title>
        <meta name="description" content="Discover how Akmo Books connects your POS, inventory, accounting, and payroll in real-time. Experience a fully integrated business ecosystem." />
      </Helmet>

      {/* Hero Section */}
      <Box 
        sx={{ 
          pt: { xs: 15, md: 20 }, 
          pb: { xs: 8, md: 10 },
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          color: 'white',
          position: 'relative'
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            style={{ textAlign: 'center' }}
          >
            <motion.div variants={itemVariants}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Activity size={48} color="#60a5fa" />
              </Box>
              <Typography 
                variant="h2" 
                component="h1" 
                fontWeight="bold" 
                gutterBottom
                sx={{
                  fontSize: { xs: '2.5rem', md: '4rem' },
                  lineHeight: 1.2,
                }}
              >
                The Connected ERP
              </Typography>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <Typography variant="h5" sx={{ mb: 4, mx: 'auto', fontWeight: 300, lineHeight: 1.6, opacity: 0.9 }}>
                A Fully Integrated Ecosystem
              </Typography>
              <Typography variant="body1" sx={{ mx: 'auto', fontSize: '1.1rem', lineHeight: 1.8, opacity: 0.8, maxWidth: '800px' }}>
                Most business software forces you to enter the same data twice. Akmo Books is built differently. Our modules communicate in real-time, meaning an action in sales automatically updates your inventory, your accounting, and your analytics.
              </Typography>
            </motion.div>
          </motion.div>
        </Container>
      </Box>

      {/* Content Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {integrations.map((module, index) => (
              <Grid item xs={12} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6 }}
                >
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: { xs: 4, md: 6 }, 
                      borderRadius: 4,
                      border: '1px solid',
                      borderColor: 'divider',
                      position: 'relative',
                      overflow: 'hidden',
                      bgcolor: 'white'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 4, flexWrap: 'wrap', gap: 3 }}>
                      <Box sx={{ 
                        p: 2, 
                        borderRadius: 3, 
                        bgcolor: 'rgba(71, 118, 230, 0.08)',
                        display: 'flex'
                      }}>
                        {module.icon}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: '300px' }}>
                        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ color: 'text.primary' }}>
                          {module.title}
                        </Typography>
                        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, lineHeight: 1.6 }}>
                          <strong>Function:</strong> {module.function}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ pl: { xs: 0, md: 9 } }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: 'primary.main', mb: 3 }}>
                        How it Integrates:
                      </Typography>
                      
                      <Grid container spacing={4}>
                        {module.triggers.map((trigger, i) => (
                          <Grid item xs={12} md={6} key={i}>
                            <Box sx={{ 
                              p: 3, 
                              borderRadius: 3, 
                              bgcolor: '#f8fafc',
                              height: '100%',
                              borderLeft: '4px solid',
                              borderColor: 'primary.main'
                            }}>
                              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ArrowRight size={18} color={theme.palette.primary.main} />
                                {trigger.name}
                              </Typography>
                              <Typography variant="body1" color="text.secondary" sx={{ pl: 3.5 }}>
                                {trigger.desc}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Features;
